import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { useNavigate } from '@tanstack/react-router';
import type { Row } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import type React from 'react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { type DragSourceMonitor, useDrag } from 'react-dnd';
import assetsByCollectionIdPlaylist from 'src/api/collection/assetsByCollectionIdPlaylist';
import editCollection from 'src/api/collection/editCollection';
import { type ParsedSuperViewData, parseSuperView, type SuperViewResponse } from 'src/api/viewModels/data';
import { HOME_PATH } from 'src/constants';
import type { PlaylistAsset } from 'src/context/CreatePlayListContext';
import CreatePlayListContext from 'src/context/CreatePlayListContext';
import { buildSuperView } from 'src/dynamoQueries/superView';
import viewStateVar from 'src/store/viewState';
import { MediaCardPreviewModal } from '../AkomiMediaCard/MediaCardPreviewModal';
import { getCellClassName, getListViewColumnStyle, getListViewFillColumnId } from './ListViewColumnSizing';
import { ListViewRowContext } from './ListViewRowContext';
import type {
  AssetListViewRow,
  DensityConfig,
  HighlightTerms,
  ListViewRow,
  ListViewRowContextValue,
} from './listView.types';

interface ListViewAssetRowProps {
  row: Row<ListViewRow>;
  asset: AssetListViewRow;
  index: number;
  classes: Record<string, string>;
  density: DensityConfig;
  highlightTerms: HighlightTerms;
}

const ListViewAssetRow: React.FC<ListViewAssetRowProps> = ({ row, asset, index, classes, density, highlightTerms }) => {
  const { itemId, permissions, highlights } = asset;

  const viewState = useReactiveVar(viewStateVar);
  const [showPreview, setShowPreview] = useState(false);
  const navigate = useNavigate();
  const openEditCollectionRef = useRef<boolean>(viewState.openEditCollection);
  const selectedCollectionIdRef = useRef<string | null>(viewState.selectedCollectionId);
  const { setCreateAddedAssets } = useContext(CreatePlayListContext);

  // SuperView query — cache-first so density remounts serve from Apollo cache
  const viewModels = viewState.viewModels;
  const ViewMediaCardListView = viewModels?.library?.ViewMediaCardListView;

  const { data: superView, loading: loadingSuperView } = useQuery<SuperViewResponse>(buildSuperView(), {
    skip: !itemId || !ViewMediaCardListView?.node?.componentName,
    variables: { globalId: itemId, viewComponentName: ViewMediaCardListView?.node?.componentName },
    fetchPolicy: 'cache-first',
  });

  // Parse superView fields once (used in render + DnD end handler)
  const parsedFields = useMemo(() => parseSuperView(superView), [superView]);

  // Build a slug-keyed map from parsedFields for cell components
  const fieldsBySlug = useMemo(() => {
    const map = new Map<string, ParsedSuperViewData>();
    if (parsedFields) {
      for (const field of parsedFields) {
        if (field.slug) map.set(field.slug, field);
      }
    }
    return map;
  }, [parsedFields]);

  // Track collection editing state via refs (for DnD end handler)
  useEffect(() => {
    selectedCollectionIdRef.current = viewState.selectedCollectionId;
  }, [viewState.selectedCollectionId]);

  useEffect(() => {
    openEditCollectionRef.current = viewState.openEditCollection;
  }, [viewState.openEditCollection]);

  // Edit collection mutation (for DnD into collection)
  const [addItemHandler] = useMutation(editCollection, {
    fetchPolicy: 'network-only',
    refetchQueries: [assetsByCollectionIdPlaylist],
  });

  const addItemCollectionHandler = (selectedAssetForAdding: string): void => {
    addItemHandler({
      variables: {
        collectionId: selectedCollectionIdRef.current,
        assetIds: selectedAssetForAdding,
        collectionType: 'PLAYLIST',
        autoSeq: true,
      },
    });
  };

  // Navigation to media page
  const handleVideoClick = (id: string): void => {
    navigate({ to: `${HOME_PATH}/media-page/${id}` });
    viewStateVar({
      ...viewState,
      mediaPageViaPlaylist: false,
      mediaPageViaLibrary: true,
    });
  };

  // Drag & Drop
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'CARD_AKOMI',
      item: { id: itemId },
      collect: (monitor: DragSourceMonitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
      end: (_dragItem: unknown, monitor: DragSourceMonitor) => {
        if (monitor.didDrop() && !openEditCollectionRef.current) {
          const displayName = fieldsBySlug.values().next().value?.show || undefined;
          setCreateAddedAssets((ids) => [...ids, { id: itemId, name: displayName } as PlaylistAsset]);
        }
        if (monitor.didDrop() && openEditCollectionRef.current) {
          addItemCollectionHandler(itemId);
        }
      },
    }),
    [itemId, fieldsBySlug],
  );

  // Compute column styles for all visible cells
  const visibleCells = row.getVisibleCells();
  const visibleColumnIds = visibleCells.map((cell) => cell.column.id);
  const fillColumnId = getListViewFillColumnId(visibleColumnIds);
  const cellStyleById = Object.fromEntries(
    visibleCells.map((cell) => [
      cell.column.id,
      getListViewColumnStyle(cell.column.id, cell.column.getSize(), fillColumnId),
    ]),
  ) as Record<string, React.CSSProperties>;

  // Selection state
  const isSelected = viewState.selectedAssets.includes(itemId);
  const isOdd = index % 2 === 1;

  const rowClassNames = [
    classes.bodyRow,
    isOdd ? classes.bodyRowOdd : '',
    isSelected ? classes.bodyRowSelected : '',
    isDragging ? classes.bodyRowDragging : '',
  ]
    .filter(Boolean)
    .join(' ');

  const contextValue: ListViewRowContextValue = {
    rowType: 'asset',
    itemId,
    permissions,
    highlights,
    fieldsBySlug,
    loading: loadingSuperView,
    highlightTerms,
    density,
    classes,
    handleNavigate: handleVideoClick,
    onShowPreview: () => setShowPreview(true),
  };

  return (
    <ListViewRowContext.Provider value={contextValue}>
      <tr ref={drag as unknown as React.Ref<HTMLTableRowElement>} className={rowClassNames} style={{ cursor: 'move' }}>
        {visibleCells.map((cell) => (
          <td
            key={cell.id}
            className={getCellClassName(cell.column.id, classes, cell.column.columnDef.meta)}
            style={cellStyleById[cell.column.id]}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))}
      </tr>
      {showPreview && <MediaCardPreviewModal itemID={itemId} pagination={true} onClose={() => setShowPreview(false)} />}
    </ListViewRowContext.Provider>
  );
};

export default ListViewAssetRow;
